import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Link, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import FeatherIcon from 'feather-icons-react';
import { Button, Modal, Descriptions, Tag } from 'antd';
import AdvancedTable from '../../components/AdvancedTable';
import { GetOrderReportApi, DownloadOrderHistoryApi } from '../../utils/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { getColumns } from './columns';
import { filterConfig, summaryConfig, defaultSort } from './config';
import dayjs from 'dayjs';
import { calculateBilling, getPaymentMode } from '../../utils/billingUtils';
import { convertToCSV, downloadCSV } from '../../utils/csvUtils';
import { notification } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { matchPermissions } from '../../utils/Permission';
import { PERMISSIONS } from '../../constants';


const OrderReportList = () => {
  const { userDetails } = useSelector(state => state?.auth);
  const location = useLocation();
  const params = queryString.parse(location.search);

  // Permissions
  const matchPermissionsForUser = matchPermissions(userDetails);
  const canView = matchPermissionsForUser(PERMISSIONS.REPORTS.ORDER_REPORTS);
  const canExport = matchPermissionsForUser(PERMISSIONS.REPORTS.ORDER_REPORTS_EXPORT);
  const [visibleData, setVisibleData] = useState([]);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const handleInvoiceClick = useCallback(row => {
    setSelectedRow(row);
    setInvoiceModalVisible(true);
  }, []);

  const handleInvoiceModalClose = useCallback(() => {
    setInvoiceModalVisible(false);
    setSelectedRow(null);
  }, []);

  // Memoize columns
  const columns = useMemo(() => {
    const baseColumns = getColumns(params);
    const invoiceColumn = {
      id: 'invoice',
      name: 'Invoice',
      selector: row => row.invoice?._id || '',
      cell: row => (
        <Button type="link" size="small" onClick={() => handleInvoiceClick(row)}>
          View Invoice
        </Button>
      ),
      width: 120,
      sortable: false,
    };
    return [...baseColumns, invoiceColumn];
  }, [params?.page, params?.perPage, handleInvoiceClick]);

  // Fetch data function
  const fetchOrderReports = useCallback(
    async ({ page, perPage, sortField, sortOrder, search, ...filters }) => {
      try {
        const formatToYYYYMMDD = value => dayjs(value).format('YYYY-MM-DD');

        const queryParams = {
          page,
          limit: perPage,
          ...(sortField && { sortField }),
          ...(sortOrder && { sortOrder }),
          ...(search && { search }),
          ...filters,
          ...(filters.dateRange_start && {
            paymentStartDate: formatToYYYYMMDD(filters.dateRange_start),
          }),
          ...(filters.dateRange_end && { paymentEndDate: formatToYYYYMMDD(filters.dateRange_end) }),
        };

        // Remove original dateRange keys if mapped
        if (queryParams.dateRange_start) delete queryParams.dateRange_start;
        if (queryParams.dateRange_end) delete queryParams.dateRange_end;

        const response = await GetOrderReportApi({ Query: queryParams });

        const resultData = response?.data?.data?.[0]?.docs || [];
        setVisibleData(resultData);

        return {
          data: resultData,
          total: response?.data?.data?.[0]?.totalDocs || 0,
        };
      } catch (error) {
        console.error('Error fetching order reports:', error);
        setVisibleData([]);
        return { data: [], total: 0 };
      }
    },
    []
  );

  // Export CSV function
  const handleExportCSV = useCallback(async () => {
    setExportLoading(true);
    try {
      const formatToYYYYMMDD = value => dayjs(value, 'DD-MM-YYYY').isValid()
        ? dayjs(value, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : dayjs(value).format('YYYY-MM-DD');

      // Get current filters from URL params
      const currentParams = queryString.parse(location.search);

      const queryParams = {
        ...currentParams,
        ...(currentParams.dateRange_start && {
          paymentStartDate: formatToYYYYMMDD(currentParams.dateRange_start),
        }),
        ...(currentParams.dateRange_end && {
          paymentEndDate: formatToYYYYMMDD(currentParams.dateRange_end)
        }),
      };

      // Remove original dateRange keys if mapped
      if (queryParams.dateRange_start) delete queryParams.dateRange_start;
      if (queryParams.dateRange_end) delete queryParams.dateRange_end;
      // Remove pagination params for full export if backend supports it
      delete queryParams.page;
      delete queryParams.perPage;

      const queryStr = '?' + queryString.stringify(queryParams);
      const response = await DownloadOrderHistoryApi({ Query: queryStr });
      const rawData = response?.data;

      // Case 1: API already returns CSV string
      if (typeof rawData === 'string' && rawData.trim().length > 0 && (rawData.includes(',') || rawData.includes('\n'))) {
        downloadCSV(rawData, `Order_History_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
        notification.success({
          message: 'Export Successful',
          description: 'CSV file downloaded from server.',
        });
        return;
      }

      // Case 2: API returns JSON data that needs conversion
      const exportData = rawData?.data?.[0]?.docs || rawData?.data || (Array.isArray(rawData) ? rawData : []);

      if (!Array.isArray(exportData) || exportData.length === 0) {
        notification.info({ message: 'No data to export' });
        return;
      }

      // Filter out columns we don't want in CSV (like the Invoice button column)
      const exportColumns = columns.filter(col => col.id !== 'invoice' && col.id !== 'sr');

      const csv = convertToCSV(exportData, exportColumns);
      downloadCSV(csv, `Order_History_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);

      notification.success({
        message: 'Export Successful',
        description: `Exported ${exportData.length} records.`,
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      notification.error({
        message: 'Export Failed',
        description: 'Failed to download order history. Please try again.',
      });
    } finally {
      setExportLoading(false);
    }
  }, [location.search, columns]);

  // Calculate total collected amount from visible rows
  const totalCollectedAmount = useMemo(() => {
    return visibleData.reduce((sum, row) => {
      const billing = calculateBilling(row);
      return sum + billing.installment;
    }, 0);
  }, [visibleData]);

  const { totalVisibleOrders, totalPendingAmount } = useMemo(() => {
    // For pending and order counts, we should only count each order once to be accurate
    const uniqueOrders = visibleData.reduce((acc, row) => {
      if (!acc[row.orderId]) {
        acc[row.orderId] = calculateBilling(row);
      } else {
        // Keep the one with the highest paid amount (most recent state)
        const current = calculateBilling(row);
        if (current.paid > acc[row.orderId].paid) {
          acc[row.orderId] = current;
        }
      }
      return acc;
    }, {});

    const values = Object.values(uniqueOrders);
    return {
      totalVisibleOrders: values.length,
      totalPendingAmount: values.reduce((sum, b) => sum + b.pending, 0)
    };
  }, [visibleData]);

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumbs */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={`/order-report?page=${params?.page || 1}`}>Order Report</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Order Report List</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Advanced Table */}
          {canView ? (
            <AdvancedTable
              resource="orderReport"
              columns={columns}
              fetchData={fetchOrderReports}
              enableSearch={false}
              enableFilters
              enableExport={false}
              enableSummary
              enableColumnManager
              filterConfig={filterConfig}
              summaryConfig={summaryConfig}
              defaultPageSize={10}
              enableHorizontalScroll={true}
              defaultSort={defaultSort}
              density="comfortable"
              stickyHeader
              striped
              highlightOnHover
              tableId="order-report-table"
              persistState
              exportFileName="order_report_export"
              customActions={
                canExport && (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportCSV}
                    loading={exportLoading}
                    style={{ backgroundColor: '#059669', borderColor: '#059669' }}
                  >
                    Export CSV
                  </Button>
                )
              }
            />
          ) : (
            <div className="permission-denied-message">
              You do not have permission to view order reports.
            </div>
          )}

          <Modal
            title="Order & Invoice Details"
            open={invoiceModalVisible}
            onCancel={handleInvoiceModalClose}
            footer={[
              <Button key="close" onClick={handleInvoiceModalClose}>
                Close
              </Button>,
            ]}
            width="90%"
            style={{ maxWidth: 850 }}
            zIndex={9999}
          >
            {selectedRow && (() => {
              const billing = calculateBilling(selectedRow);
              const invoice = selectedRow.invoice;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Financial Summary Banner */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-around', padding: '15px',
                    background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Total Bill</div>
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>₹{billing.total.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Discount</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#F97316' }}>₹{billing.discountValue || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Total Paid</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#059669' }}>₹{billing.paid.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Balance Due</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: billing.pending > 0 ? '#dc2626' : '#64748b' }}>₹{billing.pending.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Latest Collection</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#389e0d' }}>₹{billing.installment.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Status</div>
                      <Tag color={billing.isFullyPaid ? 'green' : 'orange'}>{billing.isFullyPaid ? 'FULLY PAID' : 'PARTIAL'}</Tag>
                    </div>
                  </div>

                  {/* Detailed Info */}
                  <Descriptions title="Customer & Booking Information" bordered column={{ xs: 1, sm: 1, md: 2 }} size="small">
                    <Descriptions.Item label="Customer Name">{selectedRow.additionalInfo?.name || invoice?.customerName}</Descriptions.Item>
                    <Descriptions.Item label="Phone Number">{selectedRow.additionalInfo?.phoneNo || invoice?.phoneNo}</Descriptions.Item>
                    <Descriptions.Item label="Booking Type">{selectedRow.bookingFor}</Descriptions.Item>
                    <Descriptions.Item label="Booking Period">
                      {selectedRow.additionalInfo?.startDate || invoice?.startDate} to {selectedRow.additionalInfo?.endDate || invoice?.endDate}
                    </Descriptions.Item>
                    <Descriptions.Item label="Payment Mode">
                      <Tag color="blue">{billing.paymentMode.toUpperCase()}</Tag>
                      {getPaymentMode(selectedRow) === 'cash' && <span style={{ fontSize: '10px', color: '#059669', marginLeft: '5px' }}>(Cash Collected at Counter)</span>}
                    </Descriptions.Item>
                    <Descriptions.Item label="Order Status">{selectedRow.orderStatus}</Descriptions.Item>
                    <Descriptions.Item label="Order ID" span={2}><code style={{ fontSize: '11px' }}>{selectedRow.orderId}</code></Descriptions.Item>
                  </Descriptions>

                  {invoice && (
                    <Descriptions title="Initial Invoice Record" bordered column={{ xs: 1, sm: 1, md: 2 }} size="small">
                      <Descriptions.Item label="Invoice #">{invoice.invoiceNumber}</Descriptions.Item>
                      <Descriptions.Item label="Invoice Date">{invoice.invoiceDate}</Descriptions.Item>
                      <Descriptions.Item label="Invoice Initial Paid">₹{parseFloat(invoice.paidAmount || 0).toLocaleString('en-IN')}</Descriptions.Item>
                      <Descriptions.Item label="Invoice Links">
                        {invoice.invoiceLink?.map((link, idx) => (
                          <a key={idx} href={`${process.env.REACT_APP_DOCS_URL}${link}`} target="_blank" rel="noopener noreferrer" style={{ marginRight: '10px' }}>
                            PDF {idx + 1}
                          </a>
                        ))}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </div>
              );
            })()}
          </Modal>
        </div>
      </div>
    </>
  );
};

export default OrderReportList;
